import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { ProductsSubprojectService } from '../products-subproject/products-subproject.service';

export interface GuidLookupItem {
  id: string;
  label: string;
  raw: any;
}

export interface GuidLookupSource {
  key: string;
  label: string;
  idField?: string;
  labelFields?: string[];
  fetch: (query: string, page: number, limit: number) => Promise<any>;
  clientFilter?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GuidLookupService {
  private readonly sources: GuidLookupSource[];

  constructor(
    private apiService: ApiService,
    private productsService: ProductsSubprojectService,
  ) {
    this.sources = this.buildSources();
  }

  listSources(): GuidLookupSource[] {
    return [...this.sources];
  }

  resolveSourceKey(fieldKey?: string, baseTable?: string): string | null {
    const normalizedKey = (fieldKey || '').trim();
    const keyLower = normalizedKey.toLowerCase();
    const baseLower = (baseTable || '').toLowerCase();

    if (!normalizedKey && baseLower) {
      if (baseLower.includes('rewardsbrand')) return 'rewardsBrands';
      if (baseLower.includes('rewardscampaign')) return 'rewardsCampaigns';
      if (baseLower.includes('rewardspurchased')) return 'rewardsPurchased';
      if (baseLower.includes('rewards')) return 'rewards';
      if (baseLower.includes('vendorproduct')) return 'vendorProducts';
      if (baseLower.includes('uservendorproduct')) return 'userVendorProducts';
    }

    if (/^(user|parent|child|member|owner|approver)id$/i.test(normalizedKey) || keyLower.endsWith('userid')) {
      return 'users';
    }
    if (keyLower.endsWith('teamid')) {
      return 'teams';
    }
    if (keyLower.endsWith('partnerid')) {
      return 'partners';
    }
    if (keyLower.endsWith('brandid')) {
      if (baseLower.includes('reward')) {
        return 'rewardsBrands';
      }
      return 'partners';
    }
    if (keyLower.endsWith('campaignid')) {
      return 'rewardsCampaigns';
    }
    if (keyLower.endsWith('rewardid')) {
      return baseLower.includes('purchased') ? 'rewardsPurchased' : 'rewards';
    }
    if (keyLower.endsWith('vendorproductid')) {
      return 'vendorProducts';
    }
    if (keyLower.endsWith('uservendorproductid')) {
      return 'userVendorProducts';
    }
    if (keyLower.endsWith('productid')) {
      return 'products';
    }

    return null;
  }

  async search(sourceKey: string, query: string, page = 0, limit = 50): Promise<GuidLookupItem[]> {
    const source = this.sources.find((s) => s.key === sourceKey);
    if (!source) {
      return [] as GuidLookupItem[];
    }

    const res = await source.fetch(query, page, limit);
    const items = this.normalizeItems(res);
    const mapped = items.map((item) => this.mapItem(source, item));
    const filtered = source.clientFilter && query ? this.filterItems(mapped, query) : mapped;
    return filtered.slice().sort((a, b) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase()));
  }

  private buildSources(): GuidLookupSource[] {
    return [
      {
        key: 'users',
        label: 'Users',
        labelFields: ['firstName', 'lastName', 'email', 'username'],
        fetch: (query, page, limit) =>
          this.apiService.get(`users?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'teams',
        label: 'Teams',
        labelFields: ['name', 'teamName'],
        fetch: (query, page, limit) =>
          this.apiService.get(`teams?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'partners',
        label: 'Partners',
        labelFields: ['name', 'shortName', 'partnerName'],
        fetch: () => this.apiService.get('public/partners'),
        clientFilter: true,
      },
      {
        key: 'rewardsBrands',
        label: 'Rewards Brands',
        labelFields: ['name', 'accountId'],
        fetch: (query, page, limit) =>
          this.apiService.get(`admin/reward/brands?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'rewardsCampaigns',
        label: 'Rewards Campaigns',
        labelFields: ['campaignName', 'brandId'],
        fetch: (query, page, limit) =>
          this.apiService.get(`admin/reward/campaigns?nofilter=1&filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'rewards',
        label: 'Rewards',
        labelFields: ['name'],
        fetch: (query, page, limit) =>
          this.apiService.get(`admin/reward/rewards?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'rewardsPurchased',
        label: 'Rewards Purchased',
        labelFields: ['title', 'rewardId'],
        fetch: (query, page, limit) =>
          this.apiService.get(`admin/rewards/purchasedRewards?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'vendorProducts',
        label: 'Vendor Products',
        labelFields: ['productName', 'productSlug'],
        fetch: (query, page, limit) =>
          this.apiService.get(`vendors/products/all?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'userVendorProducts',
        label: 'User Vendor Products',
        labelFields: ['productName', 'productSlug', 'userId'],
        fetch: (query, page, limit) =>
          this.apiService.get(`vendors/userproducts/all?filter=${encodeURIComponent(query || '')}&page=${page}&limit=${limit}`),
      },
      {
        key: 'products',
        label: 'Products',
        labelFields: ['name', 'stripeProductId'],
        fetch: async () => {
          const result = await this.productsService.getActiveProducts();
          return (result as any)?.data || [];
        },
        clientFilter: true,
      },
    ];
  }

  private normalizeItems(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (res && Array.isArray(res.data)) {
      return res.data;
    }
    if (res && Array.isArray(res.items)) {
      return res.items;
    }
    if (res && Array.isArray(res.results)) {
      return res.results;
    }
    if (res && Array.isArray(res.rows)) {
      return res.rows;
    }
    return [];
  }

  private mapItem(source: GuidLookupSource, item: any): GuidLookupItem {
    const idField = source.idField || 'id';
    const id = item[idField] || item.id || item.uuid || item.guid || '';
    const label = this.buildLabel(source, item) || id || '(unknown)';
    return { id, label, raw: item };
  }

  private buildLabel(source: GuidLookupSource, item: any): string {
    const fields = source.labelFields || [];
    const values = fields.map((field) => item?.[field]).filter(Boolean);
    return values.join(' ').trim();
  }

  private filterItems(items: GuidLookupItem[], query: string): GuidLookupItem[] {
    const needle = query.toLowerCase();
    return items.filter((item) =>
      (item.id || '').toLowerCase().includes(needle) || (item.label || '').toLowerCase().includes(needle),
    );
  }
}
